import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, from, of, switchMap, tap } from 'rxjs';
import { CachingService } from '../caching.service';
import { ToastController } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { EnvironmentService } from '../environment.service';
@Injectable({
  providedIn: 'root',
})
export class ApiServiceService {
  connected = true;

  get CurrEnv() {
    let connectFor = localStorage.getItem('connectFor');
    let env =
      connectFor === 'beta' ? environment.global : environment.SMBT_CLINICS;
    return env;
  }
  get orgId() {
    return localStorage.getItem('orgId');
  }
  constructor(
    private http: HttpClient,
    private router: Router,
    private cachingService: CachingService,
    private toastController: ToastController,
    private envServ: EnvironmentService
  ) {
    this.initializeStorage();
    this.toastController.create({ animated: false }).then((t) => {
      t.present();
      t.dismiss();
    });
  }
  private async initializeStorage() {
    try {
      await this.cachingService.initStorage();
    } catch (error) {
      console.error('Error initializing storage:', error);
    }
  }

  getHeaders(isFormData = false): HttpHeaders {
    let headers = new HttpHeaders();
    let token = JSON.parse(localStorage.getItem('authToken') || '');
    // Remove double quotes from the token if present
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const loggedInUser = JSON.parse(localStorage.getItem('authUser') || '{}');

    let orgId = `${loggedInUser?.organization_id || 0}`,
      locId = `${
        loggedInUser?.assign_multi_location_ids
          ? loggedInUser?.assign_multi_location_ids.split(',')[0]
          : 0
      }`;

    headers = headers.append('x-organization-id', orgId);
    headers = headers.append('x-location-id', locId);
    if (!isFormData) {
      headers = headers.append('Content-Type', 'application/json');
    }

    if (token !== null) {
      headers = headers.append('Authorization', 'Bearer ' + token);
    }
    return headers;
  }
  callAndCache(url: string, urlParams: HttpParams): Observable<any> {
    return this.http
      .get(url, {
        headers: this.getHeaders(),
        params: urlParams,
      })
      .pipe(
        tap((res) => {
          // Store our new data
          this.cachingService.cacheRequest(url, res);
        })
      );
  }

  getRequestUrl(url: string, urlParams: HttpParams): Observable<any> {
    return this.http
      .get(this.CurrEnv.basePath + url, {
        headers: this.getHeaders(),
        params: urlParams,
      })
      .pipe(
        tap((res) => {
          // Store our new data
          this.cachingService.cacheRequest(url, res);
        })
      );
  }

  getData(
    url: string,
    urlParams?: HttpParams | any,
    forceRefresh: boolean = true
  ): Observable<any> {
    return this.getDData(
      this.CurrEnv.basePath + url,
      urlParams,
      forceRefresh
    ).pipe(catchError(this.handleError));
  }
  handleError(err: any): any {
    console.error(`catch block err --> `, err);
  }

  getMasterData(
    url: string,
    urlParams?: HttpParams | any,
    forceRefresh: boolean = true
  ): Observable<any> {
    return this.getDData(
      this.CurrEnv.masterPath + url,
      urlParams,
      forceRefresh
    ).pipe(catchError(this.handleError));
  }

  postData(url: string, body: object): Observable<any> {
    return this.http
      .post(
        this.CurrEnv.basePath + url,
        body,

        { headers: this.getHeaders() }
      )
      .pipe(catchError((err) => of(err.status)));
  }
  putData(url: string, body: object, urlParams?: HttpParams): Observable<any> {
    return this.http
      .put(
        this.CurrEnv.basePath + url,
        body,

        { headers: this.getHeaders() }
      )
      .pipe(catchError((err) => of(err.status)));
  }

  patchData(
    url: string,
    body: object,
    urlParams?: HttpParams
  ): Observable<any> {
    return this.http
      .patch(
        this.CurrEnv.basePath + url,
        body,

        { headers: this.getHeaders() }
      )
      .pipe(catchError((err) => of(err.status)));
  }
  private getDData(
    url: string,
    urlParams: HttpParams,
    forceRefresh = false
  ): Observable<any> {
    // Handle offline case
    if (!this.connected) {
      this.toastController
        .create({
          message: 'You are viewing offline data.',
          duration: 2000,
        })
        .then((toast) => {
          toast.present();
        });
      return from(this.cachingService.getCachedRequest(url));
    }

    // Handle connected case
    if (forceRefresh) {
      // Make a new API call
      return this.callAndCache(url, urlParams);
    } else {
      // Check if we have cached data
      const storedValue = from(this.cachingService.getCachedRequest(url));
      return storedValue.pipe(
        switchMap((result) => {
          if (!result) {
            // Perform a new request since we have no data
            return this.callAndCache(url, urlParams);
          } else {
            // Return cached data
            return of(result);
          }
        })
      );
    }
  }

  uploadFile(url: string, formData: FormData): Observable<any> {
    return this.http
      .post(this.CurrEnv.opdPath + url, formData, {
        headers: this.getHeaders(true),
      })
      .pipe(catchError((err) => of(err.status)));
  }

  getUploadedFiles(url: string) {
    return this.http
      .get(this.CurrEnv.basePath + url, { headers: this.getHeaders() })
      .pipe(catchError((err) => of(err.status)));
  }

  printEMR(payloadObj: any) {
    return this.http.post(
      this.CurrEnv.baseReportPath_New + `?format=pdf&reportName=EMR%20`,
      payloadObj,
      { responseType: 'blob' }
    );
  }

  post_pos_url(url: string, body: object): Observable<any> {
    return this.http
      .post(this.envServ.currentEnvironment.pos_base_url + url, body)
      .pipe(catchError((err) => of(err.status)));
  }

  get_data_hr(url: string, params: HttpParams): Observable<any> {
    return this.http
      .get(this.envServ.currentEnvironment.hr_base_url + url, { params })
      .pipe(catchError((err) => of(err.status)));
  }

  getPatientDetails(apiUrl: string, params?: HttpParams): Observable<any> {
    return this.http
      .get(this.CurrEnv.opdPath + 'mobile-app-Patient_Login/'+ apiUrl, { headers: this.getHeaders(), params })
      .pipe(catchError((err) => of(err.status)));
  }

  opdService(apiUrl: string, params?: HttpParams): Observable<any> {
    return this.http
      .get(this.CurrEnv.opdPath + apiUrl, { headers: this.getHeaders(), params })
      .pipe(catchError((err) => of(err.status)));
  }

  opdPostService(apiUrl: string, body: object): Observable<any> {
    return this.http
      .post(this.CurrEnv.opdPath + apiUrl, body, { headers: this.getHeaders() })
      .pipe(catchError((err) => of(err.status)));
  }
}
